import { basePost, baseGet, useRootStore, getIdToken } from '@qsrsoft/myq-core';

export class HttpError extends Error {
  // `body` is the parsed error response when the API sent JSON (e.g. the
  // validation errors on a 400), `url` the request path without its query.
  constructor(status, statusText, body = null, url = null) {
    super(`HTTP error! status: ${status}`);
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.url = url;
  }
}

async function httpErrorFrom(resp, url) {
  let body = null;
  try {
    body = await resp.json();
  } catch {
    // Not JSON (a gateway error page, an empty body) — status is still useful.
  }
  return new HttpError(resp.status, resp.statusText, body, url.split('?')[0]);
}

const baseURL = import.meta.env.VITE_APP_REPORT_API_BASE_URL;

// Re-export for backward compatibility
export const getToken = getIdToken;

function sanatizeUrl(url) {
  // remove the leading slash if there is one
  return url.startsWith('/') ? url.slice(1) : url;
}

export async function getAPI(url) {
  const token = await getIdToken();
  const response = await fetch(`${baseURL}/${sanatizeUrl(url)}`, {
    headers: {
      'x-auth-token': token,
    },
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

/**
 * Constructs an HTTP GET request and returns the executable request promise as well
 * as a cancel function.
 *
 * @param {string} url the URL of the API resource to query
 *
 * @returns {Object} an object containing the `request` promise and it's associated `cancel` function
 */
export async function getAPIPending(url) {
  const controller = new AbortController();
  const signal = controller.signal;

  const request = fetch(`${baseURL}/${sanatizeUrl(url)}`, {
    signal,
    headers: {
      'x-auth-token': await getIdToken(),
    },
  }).then(async (resp) => {
    if (!resp.ok) throw await httpErrorFrom(resp, url);
    return resp.json();
  });

  const cancel = () => controller.abort();

  return { request, cancel };
}

/**
 * GET a file (zip, csv...) as a Blob. The file name comes from the
 * Content-Disposition header when the API sends one.
 *
 * @returns {Promise<{ blob: Blob, fileName: string|null }>}
 */
export async function getBlobAPI(url) {
  const response = await fetch(`${baseURL}/${sanatizeUrl(url)}`, {
    headers: {
      'x-auth-token': await getIdToken(),
    },
  });

  if (!response.ok) throw await httpErrorFrom(response, url);

  const disposition = response.headers.get('content-disposition') || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return {
    blob: await response.blob(),
    fileName: match ? decodeURIComponent(match[1]) : null,
  };
}

export async function postAPI(url, body) {
  const token = await getIdToken();
  const response = await fetch(`${baseURL}/${sanatizeUrl(url)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

export async function putAPI(url, body) {
  const token = await getIdToken();
  const response = await fetch(`${baseURL}/${sanatizeUrl(url)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

export async function patchAPI(url, body) {
  const token = await getIdToken();
  const response = await fetch(`${baseURL}/${sanatizeUrl(url)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

export async function deleteAPI(url, body) {
  const token = await getIdToken();
  const response = await fetch(`${baseURL}/${sanatizeUrl(url)}`, {
    method: 'DELETE',
    headers: {
      'x-auth-token': token,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

/**
 * Constructs an HTTP POST request and returns the executable request promise as well
 * as a cancel function.
 *
 * @param {string} url the URL of the API resource to query
 * @param {Object} body the request body
 *
 * @returns {Object} an object containing the `request` promise and it's associated `cancel` function
 */
export async function postAPIPending(url, body) {
  const controller = new AbortController();
  const signal = controller.signal;
  const requestPromise = fetch(`${baseURL}/${sanatizeUrl(url)}`, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': await getIdToken(),
    },
    body: JSON.stringify(body),
  }).then(async (resp) => {
    if (!resp.ok) throw await httpErrorFrom(resp, url);
    return resp.json();
  });

  const cancel = () => controller.abort();
  return { request: requestPromise, cancel };
}

export function postConfigAPI(name, settingName, setting) {
  const store = useRootStore();
  const params = new URLSearchParams({
    appName: 'reports',
    userId: store.userId,
    settingName: `${name}/${settingName}`,
  });
  const route = `/user/settings?${params.toString()}`;
  return basePost(route, setting);
}

export async function getConfigAPI(name, settingName) {
  const store = useRootStore();
  const params = new URLSearchParams({
    appName: 'reports',
    userId: store.userId,
  });
  if (name) params.append('settingName', `${name}/${settingName}`);

  try {
    const data = await baseGet(`/user/settings?${params.toString()}`);
    return settingName ? data[settingName] : data;
  } catch (ex) {
    console.error('getConfigAPI error route', ex);
  }
}

export function deleteConfigAPI(name, settingName) {
  const store = useRootStore();
  const params = new URLSearchParams({
    appName: 'reports',
    userId: store.userId,
    settingName: `${name}/${settingName}`,
  });
  const route = `/user/settings?${params.toString()}`;
  return basePost(route, null);
}

// org
export async function postOrgConfig({ name, setting }) {
  const store = useRootStore();
  const route = `organization/org-${store.orgId}/settings?appName=reports&settingName=${name}`;
  return basePost(route, setting);
}

export async function getOrgConfig(name) {
  const store = useRootStore();
  const route = name
    ? `organization/org-${store.orgId}/settings?appName=reports&settingName=${name}`
    : `organization/org-${store.orgId}/settings?appName=reports`;

  try {
    const data = await baseGet(route);
    return data;
  } catch (ex) {
    console.error('getOrgConfig error route', route, ex);
  }
}

export async function deleteOrgConfig(name) {
  if (!name)
    throw new Error(
      'name is required, you were about to delete the whole orgs settings & make support cry :('
    );
  const store = useRootStore();
  const route = `organization/org-${store.orgId}/settings?appName=reports&settingName=${name}`;
  return basePost(route, null);
}

// Enterprise-scoped config — piggy-backs the /organization/${scopeKey}/settings
// endpoint with a synthetic scope key of `enterprise-<enterpriseClient>-<countryCode>`
// so a setting applies across every org under that (enterprise, country) tuple.
// Same pattern catalogOverrides uses (see src/stores/catalogOverrides.js).
function enterpriseScopeKey() {
  const store = useRootStore();
  const ec = store.enterpriseClient;
  if (!ec) return null;
  const cc = store.countryCode;
  return `${String(ec).toLowerCase()}-${cc ? String(cc).toLowerCase() : 'unknown'}`;
}

// getEnterpriseConfig is read by every run of the MT dashboard settings
// loader (mount, group changes, enterprise tab, mock-role preview). Memoize
// the in-flight/settled fetch per (scope, name) for the life of the page so
// those runs share one request. Saves clear the scope so the saving admin's
// own tab refetches; other users pick the change up on their next page load,
// which is what happened before memoization too.
const enterpriseConfigCache = new Map();

export function clearEnterpriseConfigCache(scopeKey = null) {
  if (!scopeKey) {
    enterpriseConfigCache.clear();
    return;
  }
  for (const cacheKey of enterpriseConfigCache.keys()) {
    if (cacheKey.startsWith(`${scopeKey}|`)) enterpriseConfigCache.delete(cacheKey);
  }
}

export async function postEnterpriseConfig({ name, setting }) {
  const key = enterpriseScopeKey();
  if (!key) throw new Error('No enterpriseClient available; cannot save enterprise config');
  const route = `organization/enterprise-${key}/settings?appName=reports&settingName=${name}`;
  const result = await basePost(route, setting);
  clearEnterpriseConfigCache(key);
  return result;
}

export async function getEnterpriseConfig(name) {
  const key = enterpriseScopeKey();
  if (!key) return null;
  const cacheKey = `${key}|${name || ''}`;
  if (enterpriseConfigCache.has(cacheKey)) return enterpriseConfigCache.get(cacheKey);
  const route = name
    ? `organization/enterprise-${key}/settings?appName=reports&settingName=${name}`
    : `organization/enterprise-${key}/settings?appName=reports`;
  const promise = (async () => {
    try {
      return await baseGet(route);
    } catch (ex) {
      console.error('getEnterpriseConfig error route', route, ex);
      // Don't pin a failed fetch for the whole session.
      enterpriseConfigCache.delete(cacheKey);
      return undefined;
    }
  })();
  enterpriseConfigCache.set(cacheKey, promise);
  return promise;
}

// Role-based config — stores widget/section settings keyed by user group
export async function getRoleConfig(roleId, name) {
  const store = useRootStore();
  const appName = `reports-role-${roleId}`;
  const route = name
    ? `organization/org-${store.orgId}/settings?appName=${appName}&settingName=${name}`
    : `organization/org-${store.orgId}/settings?appName=${appName}`;
  try {
    const data = await baseGet(route);
    return data;
  } catch (ex) {
    console.error('getRoleConfig error', ex);
  }
}

export async function postRoleConfig(roleId, name, setting) {
  const store = useRootStore();
  const appName = `reports-role-${roleId}`;
  const route = `organization/org-${store.orgId}/settings?appName=${appName}&settingName=${name}`;
  return basePost(route, setting);
}

const baseRewards = import.meta.env.VITE_APP_REWARDS_API_BASE_URL;

export async function getRewardsAPI(url) {
  const token = await getIdToken();
  const response = await fetch(`${baseRewards}/api/${sanatizeUrl(url)}`, {
    headers: {
      'x-auth-token': token,
    },
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

const baseEKS = import.meta.env.VITE_APP_REPORT_EKS_URL;

export async function getEKSAPI(url) {
  const token = await getIdToken();
  const response = await fetch(`${baseEKS}/${sanatizeUrl(url)}`, {
    headers: {
      'x-auth-token': token,
    },
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

export async function getEKSAPIPending(url) {
  const controller = new AbortController();
  const signal = controller.signal;

  const request = fetch(`${baseEKS}/${sanatizeUrl(url)}`, {
    signal,
    headers: {
      'x-auth-token': await getIdToken(),
    },
  }).then(async (resp) => {
    if (!resp.ok) throw await httpErrorFrom(resp, url);
    return resp.json();
  });

  const cancel = () => controller.abort();

  return { request, cancel };
}

export async function postEKSAPI(url, body) {
  const token = await getIdToken();
  const response = await fetch(`${baseEKS}/${sanatizeUrl(url)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

export async function putEKSAPI(url, body) {
  const token = await getIdToken();
  const response = await fetch(`${baseEKS}/${sanatizeUrl(url)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

export async function patchEKSAPI(url, body) {
  const token = await getIdToken();
  const response = await fetch(`${baseEKS}/${sanatizeUrl(url)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}

export async function deleteEKSAPI(url) {
  const token = await getIdToken();
  const response = await fetch(`${baseEKS}/${sanatizeUrl(url)}`, {
    method: 'DELETE',
    headers: {
      'x-auth-token': token,
    },
  });

  if (!response.ok) throw new HttpError(response.status, response.statusText);

  return response.json();
}
